import { FunctionComponent, useEffect, useRef, useState } from "react";
import { styles } from "../styles/themes/style";
import styled from "styled-components";
import Text from "./Text";

interface IStyledInputProps {
  $error?: boolean;
  $disabled?: boolean;
  $noBorder?: boolean;
  $isSelected?: boolean;
  $border?: string;
}

export interface IInputProps extends IStyledInputProps {
  type?: string;
  spellCheck?: boolean;
  placeholder?: string;
  description?: string;
  prefilledValue?: string;
  enableDoubleClickCopy?: boolean;
  onChangeHandler?: (e: any) => void;
  onKeyDownHandler?: (e: any) => void;
  onBlurHandler?: (e: any) => void;
  style?: any;
  maxLength?: number;

  // Any other props
  [p: string]: any;
}

interface IInputAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: any;
  onSuggestionSelect: (suggestion: any) => void;
  placeholder?: string;
  loading?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
  onBlur?: () => void;
  onFocus?: () => void;
  onEnter?: () => void;
  [p: string]: any;
}

const InputAutocomplete: FunctionComponent<IInputAutocompleteProps> = ({
  value,
  onChange,
  suggestions,
  onSuggestionSelect,
  placeholder,
  loading,
  disabled,
  style,
  onBlur,
  onFocus,
  onEnter,
  ...other
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (showSuggestions && suggestions.length > 0) {
      setHighlightedIndex(-1);
    }
  }, [suggestions, showSuggestions]);

  return (
    <AutoCompleteContainer style={{ position: "relative", ...style }}>
      <StyledAutocompleteInput
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          onChange(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={(e) => {
          onFocus && onFocus();
          if (suggestions.length > 0) setShowSuggestions(true);
        }}
        onBlur={(e) => {
          setTimeout(() => setShowSuggestions(false), 150);
          onBlur && onBlur();
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && showSuggestions && suggestions.length > 0) {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev < suggestions.length - 1 ? prev + 1 : 0
            );
          } else if (e.key === "ArrowUp" && showSuggestions && suggestions.length > 0) {
            e.preventDefault();
            setHighlightedIndex((prev) =>
              prev > 0 ? prev - 1 : suggestions.length - 1
            );
          } else if (e.key === "Enter") {
            if (showSuggestions && suggestions.length > 0 && highlightedIndex >= 0) {
              onSuggestionSelect(suggestions[highlightedIndex]);
              setShowSuggestions(false);
            } else if (onEnter) {
              onEnter();
            }
          } else if (e.key === "Escape") {
            setShowSuggestions(false);
          }
          // For all other keys, allow default behavior (do not return early)
        }}
        style={{ paddingRight: value ? 28 : undefined }}
        {...other}
      />
      {value && (
        <ClearButton
          onClick={() => {
            onChange("");
            setShowSuggestions(false);
          }}
          title="Clear"
        >
          &#10005;
        </ClearButton>
      )}
      {showSuggestions && suggestions.length > 0 && (
        <SuggestionsList ref={suggestionsRef}>
          {suggestions.map((suggestion: any, idx: number) => (
            <SuggestionItem
              key={suggestion.name + idx}
              highlighted={idx === highlightedIndex}
              onMouseDown={() => {
                onSuggestionSelect(suggestion);
                setShowSuggestions(false);
              }}
              onMouseEnter={() => setHighlightedIndex(idx)}
            >
              {suggestion.name}
            </SuggestionItem>
          ))}
        </SuggestionsList>
      )}
      {loading && <LoadingText>Loading...</LoadingText>}
    </AutoCompleteContainer>
  );
};

const AutoCompleteContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledAutocompleteInput = styled.input`
  font-family: NunitoSans;
  font-size: 14px;
  width: 100%;
  height: 34px;
  padding: 0 10px;
  line-height: 20px;
  outline: none;
  border-radius: 4px;
  ::placeholder {
    color: ${styles.colors.black300};
  }
  color: ${({ theme: { primaryColor } }) => primaryColor};
  background-color: ${({ theme: { secondaryColor } }) => secondaryColor};
  border: 2px solid ${styles.colors.black200};
  &:focus,
  &:hover {
    border: 2px solid ${styles.colors.primary600};
  }
`;

const SuggestionsList = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  background: #fff;
  border-radius: 3px;
  margin-top: 5px;
  border: 1px solid ${styles.colors.black200};
  box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
`;

const SuggestionItem = styled.div<{ highlighted: boolean }>`
  text-align: left;
  font-family: NunitoSans;
  font-size: 14px;
  font-weight: 400;
  color: ${styles.colors.black600};
  padding: 6px 20px;
  cursor: pointer;
  background: ${({ highlighted }) => (highlighted ? styles.colors.black100 : "#fff")};
  font-weight: ${({ highlighted }) => (highlighted ? "bold" : "normal")};
  &:hover {
    color: ${styles.colors.primary600};
    background-color: ${styles.colors.black100};
  }
  &:last-child {
    border-bottom: none;
  }
`;

const ClearButton = styled.span`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #aaa;
  font-size: 16px;
  z-index: 2;
  user-select: none;
`;

const LoadingText = styled(Text)`
  position: absolute;
  left: 0;
  top: 100%;
  background: #fff;
  padding: 4px 8px;
  font-size: 13px;
  color: ${styles.colors.primary600};
  z-index: 11;
`;

export default InputAutocomplete;
